import React, { useState, useEffect } from 'react'
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Collapse,
  Box,
  Chip,
} from '@material-ui/core'
import { Edit, Delete, Add, KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { useMutation, useQueryClient } from 'react-query'
import stagesTabStyles from './stagesTab.styles'
import deleteStage from '../../../Apis/stages/deleteStage'
import QueryKeys from '../../../utils/constants/queryKeys'
import EditStageModal from './EditStageModal/EditStageModal'
import { parseDate } from '../../../utils/helpers/dateHelpers/parseDate'

const useStyles = makeStyles(stagesTabStyles)

// Format group type for display
const getGroupTypeName = (type) => {
  // Add space before capital letters for display
  return type?.replace(/([A-Z])/g, ' $1').trim() || 'Unknown'
}

const StagesTab = ({ orgId, seasonId, stages, isLoading, onStageClick, onGroupClick }) => {
  const classes = useStyles()
  const queryClient = useQueryClient()

  const [openDialog, setOpenDialog] = useState(false)
  const [editingStage, setEditingStage] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [stageToDelete, setStageToDelete] = useState(null)
  const [expandedStages, setExpandedStages] = useState({})

  const deleteStageMutation = useMutation(
    (stageId) => deleteStage({ orgId, seasonId, stageId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
        setDeleteConfirmOpen(false)
        setStageToDelete(null)
      },
    }
  )

  const handleAddStage = () => {
    setEditingStage(null)
    setOpenDialog(true)
  }

  const handleEditStage = (stage) => {
    setEditingStage(stage)
    setOpenDialog(true)
  }

  const handleDeleteStage = (stage) => {
    setStageToDelete(stage)
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (stageToDelete) {
      deleteStageMutation.mutate(stageToDelete.id)
    }
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingStage(null)
  }

  const toggleStageExpansion = (stageId) => {
    setExpandedStages((prev) => ({
      ...prev,
      [stageId]: !prev[stageId],
    }))
  }

  // Initialize all stages as expanded when stages data is loaded
  useEffect(() => {
    if (stages && stages.length > 0) {
      const initialExpandedState = {}
      stages.forEach((stage) => {
        initialExpandedState[stage.id] = true
      })
      setExpandedStages(initialExpandedState)
    }
  }, [stages])

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Button
          variant='contained'
          color='primary'
          startIcon={<Add />}
          onClick={handleAddStage}
        >
          Add Stage
        </Button>
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{paddingTop: 10, paddingBottom: 10, width: 50}} />
              <TableCell style={{paddingTop: 10, paddingBottom: 10}}>Stage Name</TableCell>
              <TableCell style={{paddingTop: 10, paddingBottom: 10}}>Start Date</TableCell>
              <TableCell style={{paddingTop: 10, paddingBottom: 10}}>End Date</TableCell>
              <TableCell style={{paddingTop: 10, paddingBottom: 10}}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align='center' style={{ padding: 32 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : stages && stages.length > 0 ? (
              stages.map((stage) => {
                const isExpanded = expandedStages[stage.id]
                const groups = stage.groups || []
                const hasGroups = groups.length > 0

                return (
                  <React.Fragment key={stage.id}>
                    {/* Stage Row */}
                    <TableRow hover className={classes.stageRow}>
                      <TableCell>
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleStageExpansion(stage.id)
                          }}
                          disabled={!hasGroups}
                          className={classes.expandButton}
                        >
                          {hasGroups && (isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />)}
                        </IconButton>
                      </TableCell>
                      <TableCell onClick={() => onStageClick(stage)}>
                        {stage.name}
                        {hasGroups && (
                          <Chip
                            label={`${groups.length} ${groups.length === 1 ? 'group' : 'groups'}`}
                            size='small'
                            className={classes.groupChip}
                            color='primary'
                            variant='outlined'
                          />
                        )}
                      </TableCell>
                      <TableCell onClick={() => onStageClick(stage)}>
                        {parseDate(stage.startDate) || 'N/A'}
                      </TableCell>
                      <TableCell onClick={() => onStageClick(stage)}>
                        {parseDate(stage.endDate) || 'N/A'}
                      </TableCell>
                      <TableCell align='right'>
                        <div className={classes.actionButtons}>
                          <IconButton
                            size='small'
                            color='primary'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEditStage(stage)
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size='small'
                            color='secondary'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteStage(stage)
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Groups Row (Collapsible) */}
                    {hasGroups && (
                      <TableRow>
                        <TableCell className={classes.groupsCell} colSpan={5}>
                          <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                            <Box className={classes.groupsContainer}>
                              <Typography variant='subtitle2' gutterBottom>
                                Groups in this stage:
                              </Typography>
                              {groups.map((group) => (
                                <div
                                  key={group.id}
                                  className={classes.groupItem}
                                  onClick={() => {
                                    onStageClick(stage)
                                    if (onGroupClick) onGroupClick(group)
                                  }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <Typography variant='body2'>
                                    • {group.name}
                                  </Typography>
                                  <Chip
                                    label={getGroupTypeName(group.type)}
                                    size='small'
                                    className={classes.groupChip}
                                  />
                                </div>
                              ))}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} align='center'>
                  No stages found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Stage Modal */}
      <EditStageModal
        isOpen={openDialog}
        onClose={handleCloseDialog}
        stageData={editingStage}
        orgId={orgId}
        seasonId={seasonId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete stage &quot;{stageToDelete?.name}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color='secondary'
            variant='contained'
            disabled={deleteStageMutation.isLoading}
          >
            {deleteStageMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default StagesTab
